import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import {
    FILTER_QUERY_BATCH,
    FILTER_QUERY_SEARCH,
    FILTER_SARCH_BAR_CONFIG,
    QueryType,
    SelectType,
} from '@wdx/clmi/api-services/models';
import {
    FilterFieldDefinition,
    Query,
    QueryPaging,
    SystemEntity,
} from '@wdx/clmi/clmi-swagger-gen';
import { PaginationOptions } from '@wdx/shared/infrastructure/api-service';
import { Paging, View } from '@wdx/shared/utils';
import { Observable, Subject } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import * as filterActions from '../actions';
import { FILTER_FEATURE_KEY, FilterState } from '../reducer';
import * as filterSelectors from '../selector';

@Injectable({
    providedIn: 'root',
})
export class FilterQueryFacadeService {
    resetResults$ = new Subject();

    private store$ = inject(Store<{ [FILTER_FEATURE_KEY]: FilterState }>);

    getFilterDefinition$(
        queryType: QueryType,
        includeMetaFields = false,
    ): Observable<FilterFieldDefinition[]> {
        return this.store$
            .select(
                filterSelectors.getFilterDefinition,
                queryType as SystemEntity,
            )
            .pipe(
                tap((res) => {
                    if (!res?.data && !res?.isLoading) {
                        this.getDefinitions(queryType);
                    }
                }),
                map((res) => res?.data?.fieldDefinitions),
                filter(Boolean),
                map((filterDefs) =>
                    includeMetaFields
                        ? filterDefs
                        : filterDefs.filter(
                              (fd) =>
                                  fd?.name &&
                                  ![
                                      FILTER_QUERY_SEARCH,
                                      FILTER_QUERY_BATCH,
                                      FILTER_SARCH_BAR_CONFIG,
                                  ].includes(fd.name),
                          ),
                ),
                take(1),
            ) as Observable<FilterFieldDefinition[]>;
    }

    getResults(
        queryType: QueryType,
        filters: Query,
        paginationOptions: PaginationOptions = {},
        multipleTypes = false,
        selectType?: SelectType,
    ): void {
        if (!multipleTypes) {
            this.store$.dispatch(
                filterActions.getFilterResults({
                    queryType,
                    paginationOptions,
                    filters,
                    selectType: selectType || queryType,
                }),
            );
        }

        if (multipleTypes) {
            this.store$.dispatch(
                filterActions.getFilterResultsMergeMap({
                    queryType,
                    paginationOptions,
                    filters,
                    selectType: selectType || queryType,
                }),
            );
        }
    }

    getDefinitions(queryType: QueryType): void {
        this.store$.dispatch(
            filterActions.getFilterDefinition({
                queryType,
            }),
        );
    }

    /**
     * This will dispatch the filterActions updateFilterResults
     *
     * @param queryType | TypeSearch
     * @param paging | QueryPaging
     * @param query | Query
     */
    updateResults(
        queryType: QueryType,
        paging: QueryPaging,
        query: Query,
        selectType?: SelectType,
    ): void {
        if ((paging?.page || 1) <= (paging?.totalPages || 1)) {
            this.store$.dispatch(
                filterActions.updateFilterResults({
                    queryType,
                    paginationOptions: {
                        ...paging,
                        pageNumber: paging.page,
                    },
                    filters: query,
                    selectType: selectType || queryType,
                }),
            );
        }
    }

    resetResults(queryType: QueryType, selectType?: SelectType): void {
        this.resetResults$.next(true);
        this.store$.dispatch(
            filterActions.resetFilterResults({
                selectType: selectType || queryType,
            }),
        );
    }

    /**
     * Use this method to reset the results and fetch it again
     *
     * @param filterType: FilterType
     * @param query: Query
     */
    resetResultsGetRecords(
        queryType: QueryType,
        query: Query,
        paging?: PaginationOptions,
        multipleTypes = false,
        selectType?: SelectType,
    ): void {
        this.resetResults(queryType, selectType);
        this.getResults(queryType, query, paging, multipleTypes, selectType);
    }

    selectFirstResults$(selectType: SelectType): Observable<any> {
        return this.store$.select(
            filterSelectors.selectFirstResults,
            selectType as SystemEntity,
        );
    }

    selectFilterLoading$(selectType: SelectType): Observable<boolean> {
        return this.store$
            .select(
                filterSelectors.selectFilterLoading,
                selectType as SystemEntity,
            )
            .pipe(map(Boolean));
    }

    selectFilterError$(selectType: SelectType): Observable<boolean> {
        return this.store$
            .select(
                filterSelectors.selectFilterError,
                selectType as SystemEntity,
            )
            .pipe(map(Boolean));
    }

    selectFilterResultsSuccess$(selectType: SelectType): Observable<any> {
        return this.store$.select(
            filterSelectors.selectFilterResultsSuccess,
            selectType as SystemEntity,
        );
    }

    getFilterPaging$(selectType: SelectType): Observable<Paging | null> {
        return this.store$.select(
            filterSelectors.getFilterPaging,
            selectType as SystemEntity,
        );
    }

    setSelectedView(entityType: SystemEntity, view: View): void {
        this.store$.dispatch(
            filterActions.setSelectedView({ entityType, view }),
        );
    }

    getSelectedView$(
        entityType: SystemEntity,
    ): Observable<View | null | undefined> {
        return this.store$.select(filterSelectors.getSelectedView(entityType));
    }
}
